import React from 'react';
import styled from 'styled-components';

import UnstyledLink from '#/components/basic/Link/UnstyledLink';
import Tooltip from '#/components/basic/tooltip/Tooltip';
import {
  getSessionStatusColorVar,
  MarketSessionDisplayStatus,
} from '#/components/markets/intermittent/intermittentMarkets';
import { urls } from '#/config/constants';
import { useSelector } from '#/reduxState/storeCore';

const DotLink = styled(UnstyledLink)`
  display: inline-flex;
  text-decoration: none;
`;

const Dot = styled.span<{
  $status: MarketSessionDisplayStatus;
  $transitionVeryClose?: boolean;
}>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $status, $transitionVeryClose }) =>
    getSessionStatusColorVar($status, $transitionVeryClose)};
`;

interface Props {
  status: MarketSessionDisplayStatus;
  /** Yellow warning color within 5 min of the transition (pending states are always yellow) */
  transitionVeryClose?: boolean;
  /** Market hours, e.g. "Closes: 2026-07-17 14:00\nOpens: 2026-07-19 15:00" */
  tooltip?: string;
}

/**
 * Open (green) / transition very close or pending (yellow) / closed (red)
 * market indicator. On desktop it links to the intermittent markets docs in a
 * new tab and shows the market hours tooltip; on mobile it is a plain
 * indicator without the link and tooltip.
 */
export default function MarketSessionStatusDot({
  status,
  transitionVeryClose,
  tooltip,
}: Props) {
  const isMobile = useSelector(state => state.screen.isMobile);
  if (isMobile) {
    return <Dot $status={status} $transitionVeryClose={transitionVeryClose} />;
  }
  const dot = (
    <DotLink href={urls.intermittentMarketsDocs}>
      <Dot $status={status} $transitionVeryClose={transitionVeryClose} />
    </DotLink>
  );
  if (!tooltip) {
    return dot;
  }
  return (
    <Tooltip text={tooltip} wrapper="inlineDiv" pointer>
      {dot}
    </Tooltip>
  );
}
